import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw, X } from "lucide-react";

/**
 * Listens for "cm:sw-update-available" events dispatched by the service worker
 * registration's onUpdate callback (see src/index.js). Renders a styled bottom
 * banner with a Reload button instead of using window.confirm().
 *
 * The dispatcher passes a `reload` callback in event.detail that performs
 * skipWaiting() + window.location.reload().
 */
function ServiceWorkerUpdateBanner() {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [reloadFn, setReloadFn] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      const reload = e?.detail?.reload;
      if (typeof reload === "function") {
        setReloadFn(() => reload);
        setShow(true);
      }
    };
    window.addEventListener("cm:sw-update-available", handler);
    return () => window.removeEventListener("cm:sw-update-available", handler);
  }, []);

  const handleReload = useCallback(() => {
    if (typeof reloadFn === "function") {
      reloadFn();
    } else {
      window.location.reload();
    }
  }, [reloadFn]);

  const handleDismiss = useCallback(() => setShow(false), []);

  if (!show) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10003,
        padding: "0 12px 16px 12px",
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          maxWidth: 480,
          margin: "0 auto",
          background: "#1f2937",
          color: "#fff",
          borderRadius: 14,
          padding: "12px 14px",
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.25)",
          display: "flex",
          alignItems: "center",
          gap: 12,
          pointerEvents: "auto",
          fontFamily: "'Poppins', sans-serif",
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: "#2563eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <RefreshCw size={18} color="#fff" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>
            {t("swUpdate.title", "New version available")}
          </div>
          <div style={{ fontSize: 12.5, color: "#cbd5e1", lineHeight: 1.4 }}>
            {t("swUpdate.subtitle", "Reload to get the latest improvements.")}
          </div>
        </div>
        <button
          onClick={handleReload}
          style={{
            background: "#2563eb",
            color: "#fff",
            border: "none",
            borderRadius: 10,
            padding: "10px 14px",
            fontSize: 13.5,
            fontWeight: 600,
            cursor: "pointer",
            minHeight: 40,
            fontFamily: "inherit",
          }}
        >
          {t("swUpdate.reload", "Reload")}
        </button>
        <button
          onClick={handleDismiss}
          aria-label={t("swUpdate.dismiss", "Dismiss")}
          style={{
            background: "transparent",
            border: "none",
            color: "#94a3b8",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
            borderRadius: 8,
            flexShrink: 0,
          }}
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}

export default ServiceWorkerUpdateBanner;
